import React from "react";
import { useIntl, FormattedNumber } from "react-intl";
import { useQuery } from "@apollo/client";
import { gql } from "@apollo/client";
import { useForm } from "react-final-form";
import { Price } from "@syos-music/common";
import { SelectField } from "@syos-music/form";

import { MenuItem, CircularProgress, Typography, Grid } from "@mui/material";

const DEFAULT_FRAGMENT = gql`
  fragment ShippingMethodSelectField_defaultFragment on ShippingMethod {
    id
  }
`;

const makeQuery = (fragment = DEFAULT_FRAGMENT) => {
  const fragmentName = fragment?.definitions.find(
    (def) =>
      def.kind === "FragmentDefinition" &&
      def.typeCondition.name.value === "ShippingMethod"
  )?.name.value;

  if (!fragmentName) throw new Error("Cannot extract fragment name");

  return gql`
    query (
      $countryId: ID!
      $cartTotal: PriceInput!
      $skipPrice: Boolean!
      $skipFinalPrice: Boolean!
    ) {
      shippingMethods {
        id
        name

        label {
          en
          fr
        }

        price(countryId: $countryId) @skip(if: $skipPrice) {
          currency
          value
        }

        finalPrice(countryId: $countryId, cartTotal: $cartTotal)
          @skip(if: $skipFinalPrice) {
          currency
          value
        }

        ...${fragmentName}
      }
    }

    ${fragment}
  `;
};

export default function ShippingMethodSelectField({
  priceTaxRate,
  priceCountryId,
  cartTotal,
  fragment,
  SelectProps,
  ...props
}) {
  const intl = useIntl();
  const form = useForm();

  const query = React.useMemo(() => makeQuery(fragment), [fragment]);
  const { data, loading } = useQuery(query, {
    variables: {
      countryId: priceCountryId ?? "XX",
      cartTotal,
      skipPrice: !priceCountryId,
      skipFinalPrice: !priceCountryId || !cartTotal,
    },
  });

  React.useEffect(() => {
    const fieldState = form.getFieldState(props.name);
    const shippingMethod = data?.shippingMethods.find(
      ({ id }) => id === fieldState?.value?.id
    );

    if (!shippingMethod) return;
    form.change(props.name, shippingMethod);
  }, [form, props.name, data]);

  return (
    <SelectField
      SelectProps={{
        renderValue: (shippingMethodId) => {
          const shippingMethod = data?.shippingMethods?.find(
            ({ id }) => id === shippingMethodId
          );
          return (
            shippingMethod?.label?.[intl.locale] ?? shippingMethod?.displayMode
          );
        },
        ...SelectProps,
      }}
      {...props}
      native={false}
      parse={(shippingMethodId) =>
        data?.shippingMethods?.find(
          (shippingMethod) => shippingMethod.id === shippingMethodId
        )
      }
      format={(shippingMethod) => (loading ? null : shippingMethod?.id ?? null)}
    >
      {loading && (
        <MenuItem disabled>
          <CircularProgress size="1em" sx={{ mr: "1ch" }} />
          Loading…
        </MenuItem>
      )}

      {data?.shippingMethods.map((shippingMethod) => {
        const displayPrice = shippingMethod.finalPrice ?? shippingMethod.price;

        return (
          <MenuItem key={shippingMethod.id} value={shippingMethod.id}>
            <Grid container spacing={1} alignItems="center">
              <Grid item xs>
                <Typography>{shippingMethod.label?.[intl.locale]}</Typography>
                <Typography color="textSecondary">
                  <small>{shippingMethod.name}</small>
                </Typography>
              </Grid>

              {displayPrice != null && (
                <Grid item>
                  <Typography align="right" color="primary">
                    <strong>
                      <Price {...displayPrice} taxRate={priceTaxRate} />
                    </strong>
                  </Typography>
                  <Typography align="right" color="textSecondary">
                    <small>
                      tax : 
                      <FormattedNumber
                        value={priceTaxRate}
                        // eslint-disable-next-line react/style-prop-object
                        style="percent"
                        minimumFractionDigits={1}
                        maximumFractionDigits={1}
                      />
                    </small>
                  </Typography>
                </Grid>
              )}
            </Grid>
          </MenuItem>
        );
      })}
    </SelectField>
  );
}

ShippingMethodSelectField.defaultProps = {
  priceTaxRate: 0,
};
